import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useI18n, useT } from "../../../shared/i18n/i18n";
import { formatRelativeTime } from "../../../shared/lib/format-relative-time";
import { useTRPC } from "../../../shared/api/trpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

// Общая лента рабочей области.
//
// След уже писался по каждому действию, но посмотреть его можно было только
// поштучно: историю одной задачи или трейл одного токена. Вопрос, ради которого
// след и ведут, — «кто вообще что здесь менял на прошлой неделе, и что из этого
// сделали не люди» — ответа не имел.
//
// Фильтры ровно под этот вопрос: человек, токен, служебные аккаунты, тип
// события и период.

const periodOptions = [
  { hours: 24, key: "period24h" },
  { hours: 24 * 7, key: "period7" },
  { hours: 24 * 30, key: "period30" },
  { hours: null, key: "periodAll" },
] as const;

const eventTypeOptions = [
  "work_object_created",
  "work_object_archived",
  "work_object_restored",
  "field_value_changed",
  "status_changed",
  "field_definition_created",
  "field_definition_renamed",
  "field_definition_archived",
  "field_definition_configured",
  "sprint_started",
  "sprint_start_reverted",
  "sprint_closed",
  "sprint_scope_added",
  "sprint_scope_removed",
  "invitation_created",
  "invitation_revoked",
  "invitation_accepted",
  "member_role_updated",
  "member_removed",
  "team_created",
  "team_updated",
  "team_archived",
  "scope_access_updated",
];

const anyValue = "__any__";
const serviceAccountsValue = "__service_accounts__";
const machinesValue = "__machines__";
const connectionPrefix = "connection:";
const tokenPrefix = "token:";
const feedLimit = 200;

export function ActivityPanel() {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const [actor, setActor] = useState<string>(anyValue);
  // Источник: рука в интерфейсе, служебный токен или подключённое приложение.
  // Раньше фильтр был только по токенам, и действия ассистента, вошедшего по
  // OAuth, не отличались от действий человека вообще ничем.
  const [source, setSource] = useState<string>(anyValue);
  const [eventType, setEventType] = useState<string>(anyValue);
  const [periodKey, setPeriodKey] = useState<string>("period7");

  const teamQuery = useQuery(trpc.access.team.queryOptions());
  const tokensQuery = useQuery(trpc.apiTokens.list.queryOptions());
  const connectionsQuery = useQuery(trpc.oauth.connectedApps.queryOptions());
  const members = teamQuery.data?.members ?? [];
  const tokens = tokensQuery.data ?? [];
  const connections = connectionsQuery.data ?? [];

  const createdFrom = useMemo(() => {
    const period = periodOptions.find((option) => option.key === periodKey);
    return period?.hours ? new Date(Date.now() - period.hours * 3_600_000) : undefined;
  }, [periodKey]);

  const activityQuery = useQuery(
    trpc.activity.list.queryOptions({
      ...(actor === serviceAccountsValue ? { actorsAreServiceAccounts: true } : {}),
      ...(actor !== anyValue && actor !== serviceAccountsValue ? { actorUserId: actor } : {}),
      ...(source === machinesValue ? { actorIsMachine: true } : {}),
      ...(source.startsWith(tokenPrefix) ? { actorTokenId: source.slice(tokenPrefix.length) } : {}),
      ...(source.startsWith(connectionPrefix) ? { actorOauthGrantId: source.slice(connectionPrefix.length) } : {}),
      ...(eventType === anyValue ? {} : { eventTypes: [eventType] }),
      ...(createdFrom ? { createdFrom } : {}),
      limit: feedLimit,
    }),
  );
  const events = activityQuery.data ?? [];
  const tokenNameById = new Map(tokens.map((token) => [token.id, token.name]));
  const serviceAccountIds = new Set(members.filter((member) => member.isServiceAccount).map((member) => member.userId));

  // Подписи выбранных значений. Без них триггер показывает само значение —
  // именно так на экране оказывались «__any__» и «period7».
  const actorLabels = new Map<string, string>([
    [anyValue, t("activityFeed.allActors")],
    [serviceAccountsValue, t("activityFeed.serviceAccounts")],
    ...members.map((member) => [member.userId, member.displayName ?? member.email] as const),
  ]);
  const sourceLabels = new Map<string, string>([
    [anyValue, t("activityFeed.allSources")],
    [machinesValue, t("activityFeed.machinesOnly")],
    ...connections.map((connection) => [`${connectionPrefix}${connection.id}`, connection.clientName] as const),
    ...tokens.map((token) => [`${tokenPrefix}${token.id}`, token.name] as const),
  ]);
  const eventLabels = new Map<string, string>([
    [anyValue, t("activityFeed.allEvents")],
    ...eventTypeOptions.map((type) => [type, t(`webhooks.event.${type}`)] as const),
  ]);
  const periodLabels = new Map<string, string>(periodOptions.map((option) => [option.key, t(`activityFeed.${option.key}`)]));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-lg border border-border/60 bg-card p-4 text-card-foreground shadow-sm">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-sm font-medium">{t("activityFeed.title")}</h2>
        <p className="text-xs text-muted-foreground">{t("activityFeed.subtitle")}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterSelect
          label={t("activityFeed.actor")}
          labels={actorLabels}
          onValueChange={setActor}
          value={actor}
        >
          <SelectItem value={anyValue}>{t("activityFeed.allActors")}</SelectItem>
          <SelectItem value={serviceAccountsValue}>{t("activityFeed.serviceAccounts")}</SelectItem>
          {members.map((member) => (
            <SelectItem key={member.userId} value={member.userId}>
              {member.displayName ?? member.email}
              {member.isServiceAccount ? ` · ${t("activityFeed.viaService")}` : ""}
            </SelectItem>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t("activityFeed.source")}
          labels={sourceLabels}
          onValueChange={setSource}
          value={source}
        >
          <SelectItem value={anyValue}>{t("activityFeed.allSources")}</SelectItem>
          <SelectItem value={machinesValue}>{t("activityFeed.machinesOnly")}</SelectItem>
          {connections.map((connection) => (
            <SelectItem key={connection.id} value={`${connectionPrefix}${connection.id}`}>
              {connection.clientName}
            </SelectItem>
          ))}
          {tokens.map((token) => (
            <SelectItem key={token.id} value={`${tokenPrefix}${token.id}`}>
              {token.name}
            </SelectItem>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t("activityFeed.eventType")}
          labels={eventLabels}
          onValueChange={setEventType}
          value={eventType}
        >
          <SelectItem value={anyValue}>{t("activityFeed.allEvents")}</SelectItem>
          {eventTypeOptions.map((type) => (
            <SelectItem key={type} value={type}>
              {t(`webhooks.event.${type}`)}
            </SelectItem>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t("activityFeed.period")}
          labels={periodLabels}
          onValueChange={setPeriodKey}
          value={periodKey}
        >
          {periodOptions.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {t(`activityFeed.${option.key}`)}
            </SelectItem>
          ))}
        </FilterSelect>
      </div>

      {activityQuery.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("activityFeed.empty")}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t("activityFeed.filtered", { count: events.length, limit: feedLimit })}</p>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {events.map((event) => (
              <div className="flex items-baseline gap-2.5 border-b border-border/30 py-2 last:border-0" key={event.id}>
                <span className="min-w-0 basis-44 truncate text-xs">
                  {event.actorDisplayName ?? event.actorEmail ?? "—"}
                  {event.actorUserId && serviceAccountIds.has(event.actorUserId) ? (
                    <span className="text-muted-foreground"> · {t("activityFeed.viaService")}</span>
                  ) : null}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs">
                  {t(`webhooks.event.${event.eventType}`)}
                  {event.resourceTitle ? <span className="text-muted-foreground"> · {event.resourceTitle}</span> : null}
                  {event.subjectName ? <span className="text-muted-foreground"> · {event.subjectName}</span> : null}
                </span>
                {/* Через что действие пришло. Это и есть ответ на «сделал человек
                    или агент» — раньше в ленте его не было вовсе. */}
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {event.actorConnectionName
                    ? t("activityFeed.viaConnection", { name: event.actorConnectionName })
                    : event.actorTokenId
                      ? t("activityFeed.viaToken", {
                          name: tokenNameById.get(event.actorTokenId) ?? event.actorTokenId.slice(0, 8),
                        })
                      : t("activityFeed.viaApp")}
                </span>
                <span
                  className="shrink-0 text-[11px] text-muted-foreground/70"
                  title={event.correlationId ? t("activityFeed.correlationHint") : undefined}
                >
                  {formatRelativeTime(event.createdAt, t, locale)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FilterSelect({
  children,
  label,
  labels,
  onValueChange,
  value,
}: {
  children: React.ReactNode;
  label: string;
  // Подпись выбранного значения. Без неё триггер печатает само значение, и на
  // экран попадают внутренние ключи.
  labels: Map<string, string>;
  onValueChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <Select onValueChange={(next) => onValueChange(next ?? value)} value={value}>
        <SelectTrigger className="h-8 w-48 text-xs">
          <SelectValue>{labels.get(value) ?? label}</SelectValue>
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </label>
  );
}
